import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Crown, Send, CheckCircle2 } from "lucide-react";

export default function RequestCar() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    brand: "",
    model: "",
    year_range: "",
    max_budget: "",
    details: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.details) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        max_budget: form.max_budget ? parseFloat(form.max_budget) : null,
      };
      await api.post("/requests", payload);
      setSent(true);
      toast.success("Richiesta inviata! Rocco ti ricontatterà a breve.");
    } catch (err) {
      toast.error("Errore nell'invio. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="App min-h-screen flex flex-col">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center flex-1" data-testid="request-success">
          <CheckCircle2 size={60} className="text-gold mx-auto mb-6" />
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">Richiesta inviata</h1>
          <p className="text-midnight/70 text-lg leading-relaxed mb-8 max-w-xl mx-auto">
            Grazie {form.name}. Rocco valuterà personalmente la tua richiesta e ti contatterà entro 24 ore.
          </p>
          <button
            onClick={() => {
              setSent(false);
              setForm({ name: "", email: "", phone: "", brand: "", model: "", year_range: "", max_budget: "", details: "" });
            }}
            className="text-sm uppercase tracking-widest text-gold hover:underline"
            data-testid="new-request-btn"
          >
            Invia un'altra richiesta
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App min-h-screen flex flex-col" data-testid="request-page">
      <Header />

      <section className="max-w-6xl mx-auto px-5 md:px-10 py-16 md:py-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="divider-gold" />
              <span className="text-gold text-xs uppercase tracking-[0.3em] font-medium">
                Richiesta Personalizzata
              </span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold mb-6 leading-tight heading-gold-silver" data-testid="request-hero-title">
              Non trovi l'auto <span className="italic">giusta?</span>
            </h1>
            <p className="text-white/80 leading-relaxed mb-8">
              Rocco ha una rete di contatti nel mondo automobilistico. Dicci cosa cerchi e faremo il possibile per trovarla per te.
            </p>
            <div className="card-sharp p-6 surface-silver text-midnight">
              <div className="flex items-center gap-3 mb-3">
                <Crown size={18} className="text-gold" />
                <h3 className="font-display text-lg font-bold text-midnight">Il Servizio</h3>
              </div>
              <ul className="space-y-2 text-sm text-midnight font-medium">
                <li>· Ricerca personalizzata su misura</li>
                <li>· Verifica meccanica prima dell'acquisto</li>
                <li>· Trattativa trasparente e diretta</li>
                <li>· Consegna in tutta Italia</li>
              </ul>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 card-sharp p-8 md:p-10 space-y-6" data-testid="request-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Nome e Cognome *</Label>
                <Input
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="mt-2 rounded-sm"
                  data-testid="request-name-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Telefono *</Label>
                <Input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  className="mt-2 rounded-sm"
                  data-testid="request-phone-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Email *</Label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                className="mt-2 rounded-sm"
                data-testid="request-email-input"
              />
            </div>

            <div className="pt-4 border-t border-border">
              <p className="text-xs uppercase tracking-widest text-gold mb-4">Auto ricercata</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Marca</Label>
                  <Input
                    value={form.brand}
                    onChange={update("brand")}
                    placeholder="es. Audi"
                    className="mt-2 rounded-sm"
                    data-testid="request-brand-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Modello</Label>
                  <Input
                    value={form.model}
                    onChange={update("model")}
                    placeholder="es. A4 Avant"
                    className="mt-2 rounded-sm"
                    data-testid="request-model-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Anno preferito</Label>
                  <Input
                    value={form.year_range}
                    onChange={update("year_range")}
                    placeholder="es. 2018-2020"
                    className="mt-2 rounded-sm"
                    data-testid="request-year-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Budget massimo (€)</Label>
                  <Input
                    type="number"
                    value={form.max_budget}
                    onChange={update("max_budget")}
                    placeholder="es. 20000"
                    className="mt-2 rounded-sm"
                    data-testid="request-budget-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-midnight font-bold">Dettagli aggiuntivi *</Label>
              <Textarea
                required
                value={form.details}
                onChange={update("details")}
                rows={5}
                placeholder="Descrivici l'auto che stai cercando: alimentazione, cambio, colore, optional, chilometraggio..."
                className="mt-2 rounded-sm"
                data-testid="request-details-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-gold w-full flex items-center justify-center gap-2 px-6 py-4 text-sm uppercase tracking-widest font-semibold disabled:opacity-50"
              data-testid="request-submit-btn"
            >
              <Send size={16} />
              {loading ? "Invio..." : "Invia Richiesta"}
            </button>
            <p className="text-xs text-midnight/40 text-center">
              I campi contrassegnati con * sono obbligatori
            </p>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
